// File: src/services/adminApi.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Admin API service for communicating with the admin backend.

import axios from 'axios';
import { auth } from '../firebaseConfig';

// Get API endpoint from environment variable
const API_URL = process.env.REACT_APP_API_ENDPOINT || (process.env.NODE_ENV === 'development' ? 'http://localhost:8788' : 'https://api-admin.ads.defoai.com');

/**
 * Get the current user (real Firebase or mock for E2E testing).
 * @returns {object|null} User object with getIdToken method, or null
 */
const getCurrentUser = () => {
    // 1. Try real Firebase user first
    if (auth.currentUser) {
        return auth.currentUser;
    }

    // 2. Check for Mock Auth (Dev/Test only)
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_TEST_AUTH_SECRET) {
        try {
            const storedMock = localStorage.getItem('firebase-auth-mock');
            if (storedMock) {
                const parsed = JSON.parse(storedMock);
                if (parsed.isAuthenticated && parsed.user) {
                    const { uid, email } = parsed.user;
                    const secret = process.env.REACT_APP_TEST_AUTH_SECRET;
                    return {
                        ...parsed.user,
                        emailVerified: true,
                        getIdToken: async () => `${secret}:${uid}:${email}`,
                        reload: async () => {},
                        delete: async () => {},
                    };
                }
            }
        } catch (e) {
            console.warn('[adminApi] Failed to parse mock auth:', e);
        }
    }

    return null;
};

/**
 * Creates an axios instance with proper authentication headers.
 * @returns {Promise<import('axios').AxiosInstance>} Configured axios instance
 */
const createAuthenticatedClient = async () => {
    const user = getCurrentUser();
    if (!user) {
        throw new Error('No authenticated user found');
    }

    const idToken = await user.getIdToken();

    return axios.create({
        baseURL: API_URL,
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
        withCredentials: true,
        timeout: 30000,
    });
};

/**
 * Handles admin API errors and extracts meaningful error information.
 * @param {Error} err - The error object from axios.
 * @returns {object} Formatted error information.
 */
export function handleAdminApiError(err) {
    if (err.response) {
        const status = err.response.status;
        const data = err.response.data;

        if (status === 401) {
            return { type: 'UNAUTHORIZED', message: 'Authentication required. Please log in again.', status };
        }
        if (status === 403) {
            return { type: 'FORBIDDEN', message: 'You do not have permission to access this resource.', status };
        }
        if (status === 404) {
            return { type: 'NOT_FOUND', message: data?.error || 'Resource not found.', status };
        }
        if (status >= 500) {
            return { type: 'SERVER_ERROR', message: 'Server error. Please try again later.', status };
        }

        return {
            type: 'CLIENT_ERROR',
            message: data?.error || `Request failed with status ${status}`,
            status
        };
    }

    if (err.request) {
        return { type: 'NETWORK_ERROR', message: 'Network error. Please check your connection.', status: 0 };
    }

    return { type: 'UNKNOWN_ERROR', message: err.message || 'An unexpected error occurred.', status: 0 };
}

/**
 * Admin API service object containing all admin operations.
 */
export const adminApi = {
    /**
     * Fetches a paginated list of users.
     * @param {object} options - Query options (limit, cursor, includeArchived).
     * @returns {Promise<{users: Array, cursor: string, list_complete: boolean}>}
     */
    async getUsers(options = {}) {
        const client = await createAuthenticatedClient();
        const params = new URLSearchParams();
        if (options.limit) params.append('limit', options.limit);
        if (options.cursor) params.append('cursor', options.cursor);
        if (options.includeArchived) params.append('includeArchived', 'true');

        const response = await client.get(`/admin/users?${params.toString()}`);
        return response.data;
    },

    /**
     * Fetches a specific user by UID.
     * @param {string} uid - The user's UID.
     * @returns {Promise<object>} The user data.
     */
    async getUser(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}`);
        return response.data;
    },

    /**
     * Updates a user's profile.
     * @param {string} uid - The user's UID.
     * @param {object} updateData - The fields to update.
     * @returns {Promise<object>} The updated user data.
     */
    async updateUser(uid, updateData) {
        const client = await createAuthenticatedClient();
        const response = await client.put(`/admin/users/${uid}`, updateData);
        return response.data;
    },

    /**
     * Archives (soft-deletes) a user.
     * @param {string} uid - The user's UID.
     * @returns {Promise<object>} Archive confirmation.
     */
    async archiveUser(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.delete(`/admin/users/${uid}`);
        return response.data;
    },

    /**
     * Restores an archived user.
     * @param {string} uid - The user's UID.
     * @returns {Promise<object>} Restore confirmation.
     */
    async restoreUser(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/users/${uid}/restore`);
        return response.data;
    },

    /**
     * IRREVERSIBLY hard-deletes a user: removes them from Firebase Authentication
     * and purges all of their data. Distinct from archiveUser (reversible).
     * @param {string} uid - The user's UID.
     * @param {{confirm: string, reason?: string}} body - `confirm` must equal the
     *   user's email or uid (typed-confirmation gate); `reason` is a compliance note.
     * @returns {Promise<object>} { status: 'deleted'|'partial', keysDeleted, firebaseDeleted, steps }.
     */
    async deleteUser(uid, { confirm, reason } = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/users/${uid}/delete`, { confirm, reason });
        return response.data;
    },

    /**
     * Fetches the current EdenAI configuration.
     * @returns {Promise<{defaultProvider: string, defaultModel: string, imageProvider: string}>}
     */
    async getEdenAIConfig() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/edenai-config');
        return response.data;
    },

    /**
     * Updates the EdenAI configuration.
     * @param {object} config - Configuration object with defaultProvider, defaultModel, and/or imageProvider.
     * @returns {Promise<object>} The updated configuration.
     */
    async updateEdenAIConfig(config) {
        const client = await createAuthenticatedClient();
        const response = await client.put('/admin/edenai-config', config);
        return response.data;
    },

    /**
     * Syncs models and pricing data from Eden AI API.
     * @returns {Promise<object>} Sync result with summary and synced data.
     */
    async syncEdenAIModels() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/edenai-config/sync');
        return response.data;
    },

    /**
     * Fetches the current Stripe configuration.
     * @returns {Promise<{publishableKey: string, secretKey: string, webhookSecret: string, priceId: string, isConfigured: boolean}>}
     */
    async getStripeConfig() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/stripe-config');
        return response.data;
    },

    /**
     * Updates the Stripe configuration.
     * @param {object} config - Configuration object with publishableKey, secretKey, webhookSecret, priceId.
     * @returns {Promise<object>} The updated configuration.
     */
    async updateStripeConfig(config) {
        const client = await createAuthenticatedClient();
        const response = await client.put('/admin/stripe-config', config);
        return response.data;
    },

    /**
     * Fetches the current SendGrid configuration.
     * @returns {Promise<{apiKey: string, isConfigured: boolean}>}
     */
    async getSendGridConfig() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/sendgrid-config');
        return response.data;
    },

    /**
     * Updates the SendGrid configuration.
     * @param {object} config - Configuration object with apiKey.
     * @returns {Promise<object>} The updated configuration.
     */
    async updateSendGridConfig(config) {
        const client = await createAuthenticatedClient();
        const response = await client.put('/admin/sendgrid-config', config);
        return response.data;
    },

    /**
     * Verifies the SendGrid connection with provided credentials.
     * @param {object} credentials - Optional { apiKey } to verify. Uses stored key if omitted.
     * @returns {Promise<{status: string, message: string, scopes: Array}>} Verification result.
     */
    async verifySendGridConnection(credentials = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/sendgrid-config/verify', credentials);
        return response.data;
    },

    // Twilio Configuration
    async getTwilioConfig() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/twilio-config');
        return response.data;
    },

    async updateTwilioConfig(config) {
        const client = await createAuthenticatedClient();
        const response = await client.put('/admin/twilio-config', config);
        return response.data;
    },

    async verifyTwilioConnection(credentials = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/twilio-config/verify', credentials);
        return response.data;
    },

    // Slack Configuration
    async getSlackConfig() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/slack-config');
        return response.data;
    },

    async updateSlackConfig(config) {
        const client = await createAuthenticatedClient();
        const response = await client.put('/admin/slack-config', config);
        return response.data;
    },

    async verifySlackConnection(credentials = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/slack-config/verify', credentials);
        return response.data;
    },

    async getSlackNotificationHistory(limit = 50) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/slack-config/history?limit=${limit}`);
        return response.data;
    },

    /**
     * Verifies the Stripe connection with provided credentials.
     * @param {object} credentials - Optional { secretKey } to verify. Uses stored key if omitted.
     * @returns {Promise<{status: string, message: string, prices: Array}>} Verification result and prices.
     */
    async verifyStripeConnection(credentials = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/stripe-config/verify', credentials);
        return response.data;
    },

    /**
     * Fetches available Stripe prices using stored credentials.
     * @returns {Promise<{status: string, message: string, prices: Array}>} List of prices.
     */
    async getStripePrices() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/stripe-config/prices');
        return response.data;
    },

    // legacy subscription-options CRUD removed. Use mainApi.listPlans
    // mainApi.updatePlan to edit the 5-field Free + Pro plans on the main API.


    /**
     * Fetches current email configuration (master + prompts).
     * @returns {Promise<{masterConfig: object, eventPrompts: object}>}
     */
    async getEmailConfig() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/email-config');
        return response.data;
    },

    /**
     * Updates master email configuration.
     * @param {object} config - Master config object.
     * @returns {Promise<object>}
     */
    async updateMasterConfig(config) {
        const client = await createAuthenticatedClient();
        const response = await client.put('/admin/email-config/master', config);
        return response.data;
    },

    /**
     * Updates a specific event prompt.
     * @param {string} eventType
     * @param {string} prompt
     * @returns {Promise<object>}
     */
    async updateEventPrompt(eventType, prompt) {
        const client = await createAuthenticatedClient();
        const response = await client.put(`/admin/email-config/events/${eventType}`, { prompt });
        return response.data;
    },

    /**
     * Sends a test email.
     * @param {object} params - { eventType, prompt, language }
     * @returns {Promise<object>}
     */
    async sendTestEmail(params) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/email-config/test', params);
        return response.data;
    },

    /**
     * Generates email preview (without sending).
     * @param {object} params - { eventType, language, placeholders: { userName, campaignName, etc } }
     * @returns {Promise<{ subject: string, body: string, fullHtml: string }>}
     */
    async previewEmail(params) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/email-config/preview', params);
        return response.data;
    },

    /**
     * Generates content using Eden.AI.
     * @param {object} params
     * @param {string} params.prompt - The user prompt.
     * @param {string} params.systemMessage - The system context.
     * @param {object} params.schema - Optional JSON schema.
     * @param {boolean} params.jsonMode - Whether to force JSON output using json_mode param.
     * @returns {Promise<{result: string|object}>}
     */
    async generateEdenAI({ prompt, systemMessage, schema, jsonMode }) {
        const client = await createAuthenticatedClient();
        // Construct payload compatible with backend handler
        const payload = {
            prompt,
            systemMessage,
            schema,
            // Pass standard params if supported
            json_mode: jsonMode
        };
        const response = await client.post('/api/ai/generate', payload);
        return response.data;
    },

    /**
     * Fetches the documentation configuration.
     * @returns {Promise<{documents: Array}>} Array of documentation items.
     */
    async getDocumentation() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/documentation');
        return response.data;
    },

    /**
     * Updates the documentation configuration.
     * @param {object} data - { documents: Array }
     * @returns {Promise<object>} The updated documentation.
     */
    async updateDocumentation(data) {
        const client = await createAuthenticatedClient();
        const response = await client.put('/admin/documentation', data);
        return response.data;
    },

    /**
     * Audit Logs
     */
    async getAuditLogs(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/audit', { params });
        return response.data;
    },

    async getAuditLog(id) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/audit/${id}`);
        return response.data;
    },

    async exportAuditLogs(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/audit/export', {
            params,
            responseType: 'blob'
        });
        return response.data;
    },

    // deleteUserPlanInstance + token-bonus methods removed.
    // Use mainApi.updateUserSubscriptionState / mainApi.grantCredits / mainApi.deleteCredits.


    // Per-User Platform Integrations

    /**
     * Fetches platform integration status for a specific user.
     * @param {string} uid - User ID.
     * @returns {Promise<{integrations: Array}>} User's platform connections with status.
     */
    async getUserIntegrations(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}/integrations`);
        return response.data;
    },

    /**
     * Fetches a user's current rate-limit / block state across all
     * buckets (request counters, mitigation bans, per-token minute, per-user day).
     * @param {string} uid - User ID.
     * @returns {Promise<{uid: string, available: boolean, blocked: boolean, buckets: Array}>}
     */
    async getUserRateLimits(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}/rate-limits`);
        return response.data.data;
    },

    /**
     * Clears every rate-limit / mitigation key for a user, unblocking them.
     * @param {string} uid - User ID.
     * @returns {Promise<{uid: string, success: boolean, clearedKeys: number}>}
     */
    async resetUserRateLimits(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.delete(`/admin/users/${uid}/rate-limits`);
        return response.data.data;
    },

    // --- Ad Platform Integration Config APIs ---

    /**
     * Fetches all ad platform integration configurations.
     * @returns {Promise<{integrations: object}>} Integration configs keyed by platform ID.
     */
    async getAdPlatformConfigs() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/integrations');
        return response.data;
    },

    /**
     * Fetches a specific ad platform configuration.
     * @param {string} platformId - Platform ID (google-ads, meta-ads, microsoft-ads).
     * @returns {Promise<object>} Platform configuration with schema and masked secrets.
     */
    async getAdPlatformConfig(platformId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/integrations/${platformId}/config`);
        return response.data;
    },

    /**
     * Updates an ad platform configuration.
     * @param {string} platformId - Platform ID (google-ads, meta-ads, microsoft-ads).
     * @param {object} config - Configuration with credentials (secrets will be encrypted).
     * @returns {Promise<object>} Update confirmation.
     */
    async updateAdPlatformConfig(platformId, config) {
        const client = await createAuthenticatedClient();
        const response = await client.put(`/admin/integrations/${platformId}/config`, config);
        return response.data;
    },

    /**
     * Deletes an ad platform configuration.
     * @param {string} platformId - Platform ID.
     * @returns {Promise<object>} Deletion confirmation.
     */
    async deleteAdPlatformConfig(platformId) {
        const client = await createAuthenticatedClient();
        const response = await client.delete(`/admin/integrations/${platformId}/config`);
        return response.data;
    },

    /**
     * Verifies ad platform credentials.
     * @param {string} platformId - Platform ID.
     * @returns {Promise<{success: boolean, message: string, error?: string}>} Verification result.
     */
    async verifyAdPlatformConfig(platformId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/integrations/${platformId}/verify`);
        return response.data;
    },

    /**
     * Gets platform health status (b).
     * @param {string} platformId - Platform ID.
     * @returns {Promise<{data: {status, lastSuccessfulCall, errorRate24h, avgLatency24h, totalCalls24h, errors24h, connectedUsers}}>}
     */
    async getPlatformHealth(platformId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/integrations/${platformId}/health`);
        return response.data;
    },

    /**
     * Gets aggregated platform analytics (b).
     * @param {object} params - Query params (period, platformId).
     * @returns {Promise<{data: {period, platforms}}>}
     */
    async getPlatformAnalytics(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/integrations/analytics', { params });
        return response.data;
    },

    /**
     * Exports the full ad platform configuration including decrypted secrets and user connections.
     * Returns a downloadable JSON blob.
     * @param {string} platformId - Platform ID (google-ads, meta-ads, microsoft-ads).
     * @returns {Promise<Blob>} JSON blob of complete platform configuration.
     */
    async exportAdPlatformConfig(platformId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/integrations/${platformId}/export`, {
            responseType: 'blob'
        });
        return response.data;
    },

    /**
     * Exports the complete user record as JSON.
     * Includes user profile, all subscriptions, subscription data, and configuration.
     * @param {string} uid - The user's UID.
     * @returns {Promise<Blob>} JSON blob of complete user record.
     */
    async exportUserRecord(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}/export`, {
            responseType: 'blob'
        });
        return response.data;
    },

    /**
     * Exports the full user state as JSON.
     * Includes all user data: linked subscriptions, usage data, configuration, and history.
     * @param {string} uid - The user's UID.
     * @returns {Promise<Blob>} JSON blob of full user state.
     */
    async exportUserState(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}/export/full`, {
            responseType: 'blob'
        });
        return response.data;
    },

    // ==================== SCHEDULER ADMIN API ====================

    /**
     * Get global scheduler statistics.
     * @param {object} params - Query params (days).
     * @returns {Promise<{activeUsersCount: number, period: object, dailyStats: Array}>}
     */
    async getSchedulerStats(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/scheduler/stats', { params });
        return response.data;
    },

    /**
     * List users with active schedules.
     * @param {object} params - Query params (page, pageSize).
     * @returns {Promise<{users: Array, pagination: object}>}
     */
    async getSchedulerUsers(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/scheduler/users', { params });
        return response.data;
    },

    /**
     * Get scheduler details for a specific user.
     * @param {string} uid - The user's UID.
     * @returns {Promise<{uid: string, stats: object, schedules: Array, recentHistory: Array}>}
     */
    async getSchedulerUserDetail(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/scheduler/users/${uid}`);
        return response.data;
    },

    /**
     * Get recent scheduler errors across all users.
     * @param {object} params - Query params (limit).
     * @returns {Promise<{errors: Array}>}
     */
    async getSchedulerErrors(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/scheduler/errors', { params });
        return response.data;
    },

    /**
     * Create a schedule for a user.
     * @param {string} uid - The user's UID.
     * @param {object} data - Schedule data (type, interval, platformId, accountId, config).
     * @returns {Promise<object>} The created schedule.
     */
    async createUserSchedule(uid, data) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/scheduler/users/${uid}/schedules`, data);
        return response.data;
    },

    /**
     * Update a schedule for a user.
     * @param {string} uid - The user's UID.
     * @param {string} scheduleId - The schedule ID.
     * @param {object} data - Schedule update data.
     * @returns {Promise<object>} The updated schedule.
     */
    async updateUserSchedule(uid, scheduleId, data) {
        const client = await createAuthenticatedClient();
        const response = await client.put(`/admin/scheduler/users/${uid}/schedules/${scheduleId}`, data);
        return response.data;
    },

    /**
     * Delete a schedule for a user.
     * @param {string} uid - The user's UID.
     * @param {string} scheduleId - The schedule ID.
     * @returns {Promise<{success: boolean, deletedId: string}>}
     */
    async deleteUserSchedule(uid, scheduleId) {
        const client = await createAuthenticatedClient();
        const response = await client.delete(`/admin/scheduler/users/${uid}/schedules/${scheduleId}`);
        return response.data;
    },

    /**
     * Pause all schedules for a user.
     * @param {string} uid - The user's UID.
     * @returns {Promise<{success: boolean, pausedCount: number}>}
     */
    async pauseUserSchedules(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/scheduler/users/${uid}/pause`);
        return response.data;
    },

    /**
     * Resume all schedules for a user.
     * @param {string} uid - The user's UID.
     * @returns {Promise<{success: boolean, resumedCount: number}>}
     */
    async resumeUserSchedules(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/scheduler/users/${uid}/resume`);
        return response.data;
    },

    /**
     * Trigger immediate execution of a schedule.
     * @param {string} uid - The user's UID.
     * @param {string} scheduleId - The schedule ID.
     * @returns {Promise<{triggered: boolean, executionId: string}>}
     */
    async triggerUserSchedule(uid, scheduleId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/scheduler/users/${uid}/schedules/${scheduleId}/trigger`);
        return response.data;
    },

    /**
     * Reset circuit breaker for a schedule.
     * @param {string} uid - The user's UID.
     * @param {string} scheduleId - The schedule ID.
     * @returns {Promise<{success: boolean}>}
     */
    async resetScheduleCircuitBreaker(uid, scheduleId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/scheduler/users/${uid}/schedules/${scheduleId}/reset`);
        return response.data;
    },

    /**
     * Bulk reset all circuit-broken schedules across all users.
     * @returns {Promise<{success: boolean, resetCount: number, message: string}>}
     */
    async resetAllCircuitBreakers() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/scheduler/reset-circuit-breakers');
        return response.data;
    },

    /**
     * Health check a single user's scheduler DO.
     * @param {string} uid - The user's UID.
     * @param {object} opts - { repair?: boolean }
     * @returns {Promise<object>} Health check result with status, issue, repaired flag.
     */
    async healthCheckUser(uid, opts = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/scheduler/users/${uid}/health`, opts);
        return response.data;
    },

    /**
     * Bulk health check all (or specified) scheduler DOs.
     * @param {object} opts - { repair?: boolean, uids?: string[] }
     * @returns {Promise<{summary: object, results: Array, checkedAt: number}>}
     */
    async bulkHealthCheck(opts = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/scheduler/health-check', opts, { timeout: 120000 });
        return response.data;
    },

    /**
     * Bulk repair all scheduler DOs (convenience for health-check with repair=true).
     * @returns {Promise<{summary: object, results: Array, checkedAt: number}>}
     */
    async bulkRepairSchedulers() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/scheduler/repair', {}, { timeout: 120000 });
        return response.data;
    },

    /**
     * Get all schedules for a specific analysis task across all users.
     * @param {string} taskId - Analysis task ID
     * @returns {Promise<{schedules: Array, total: number}>}
     */
    async getSchedulesByTask(taskId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/scheduler/by-task/${taskId}`, { timeout: 60000 });
        return response.data;
    },

    /**
     * Migrate STATS_REFRESH schedules into PLATFORM_SYNC schedules.
     * @returns {Promise<{migrated: number, skipped: number, errors: Array, details: Array}>}
     */
    async migrateStatsRefresh() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/scheduler/migrate-stats-refresh', {}, { timeout: 120000 });
        return response.data;
    },

    // ==================== ANALYSIS TASKS ADMIN API ====================

    /**
     * Fetches all analysis tasks.
     * @returns {Promise<Array>} List of analysis tasks.
     */
    async getAnalysisTasks() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/analysis-tasks');
        return response.data;
    },

    /**
     * Fetches a specific analysis task by ID.
     * @param {string} id - The task ID.
     * @returns {Promise<object>} The analysis task data.
     */
    async getAnalysisTask(id) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/analysis-tasks/${id}`);
        return response.data;
    },

    /**
     * Creates a new analysis task.
     * @param {object} data - The analysis task data.
     * @returns {Promise<object>} The created task.
     */
    async createAnalysisTask(data) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/analysis-tasks', data);
        return response.data;
    },

    /**
     * Updates an analysis task.
     * @param {string} id - The task ID.
     * @param {object} data - The update data.
     * @returns {Promise<object>} The updated task.
     */
    async updateAnalysisTask(id, data) {
        const client = await createAuthenticatedClient();
        const response = await client.put(`/admin/analysis-tasks/${id}`, data);
        return response.data;
    },

    /**
     * Deletes an analysis task.
     * @param {string} id - The task ID.
     * @returns {Promise<object>} Deletion confirmation.
     */
    async deleteAnalysisTask(id) {
        const client = await createAuthenticatedClient();
        const response = await client.delete(`/admin/analysis-tasks/${id}`);
        return response.data;
    },

    /**
     * Tests an analysis task against a specific user.
     * @param {string} id - The task ID.
     * @param {string} userId - The user ID to test against.
     * @returns {Promise<object>} Test execution results.
     */
    async testAnalysisTask(id, userId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/analysis-tasks/${id}/test`, { userId });
        return response.data;
    },

    /**
     * Fetches statistics for a specific analysis task.
     * @param {string} id - The task ID.
     * @returns {Promise<object>} Task statistics.
     */
    async getAnalysisTaskStats(id) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/analysis-tasks/${id}/stats`);
        return response.data;
    },

    /**
     * Fetches global analysis task statistics.
     * @returns {Promise<object>} Global stats across all analysis tasks.
     */
    async getGlobalAnalysisStats() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/analysis-tasks/stats');
        return response.data;
    },

    /**
     * Toggles the master switch for all analysis tasks.
     * @param {boolean} enabled - Whether to enable or disable all tasks.
     * @returns {Promise<object>} Confirmation with new state.
     */
    async toggleMasterSwitch(enabled) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/analysis-tasks/master-switch', { enabled });
        return response.data;
    },

    /**
     * Fetches the shared Tool Core catalog (the 52 MCP/v1 tools) grouped by
     * scope. Single source of truth for the tool picker + validation — replaces
     * the hardcoded frontend tool lists.
     * @returns {Promise<{tools: Array, byScope: object}>}
     */
    async getToolCatalog() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/analysis-tasks/tool-catalog');
        return response.data;
    },

    // Template endpoints (Issue #64)

    /**
     * Fetches all analysis task templates.
     * @returns {Promise<object>} Object with templates array.
     */
    async getTemplates() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/analysis-tasks/templates');
        return response.data;
    },

    /**
     * Fetches a specific template by ID.
     * @param {string} id - The template ID.
     * @returns {Promise<object>} Full template object with config and documentation.
     */
    async getTemplate(id) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/analysis-tasks/templates/${id}`);
        return response.data;
    },

    /**
     * Calls the AI assistant for analysis task configuration help.
     * @param {object} params - { action, instruction, currentConfig, conversationHistory }
     * @returns {Promise<object>} AI response with suggestion and/or message.
     */
    async aiAssistAnalysisTask({ action, instruction, currentConfig, conversationHistory }) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/analysis-tasks/ai-assist', {
            action,
            instruction,
            currentConfig,
            conversationHistory,
        });
        return response.data;
    },

    /**
     * Clones an analysis task.
     * @param {string} id - The task ID to clone.
     * @returns {Promise<object>} The cloned task.
     */
    async cloneAnalysisTask(id) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/analysis-tasks/${id}/clone`);
        return response.data;
    },

    // ==================== SCHEDULE RECONCILIATION API (Issue #81) ====================

    /**
     * Triggers immediate reconciliation for an analysis task.
     * @param {string} taskId - The task ID.
     * @returns {Promise<object>} Reconciliation report { added, removed, updated, skipped, errors }.
     */
    async reconcileTask(taskId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/analysis-tasks/${taskId}/reconcile`, {}, { timeout: 120000 });
        return response.data;
    },

    /**
     * Re-syncs (reconciles) EVERY active analysis task. Re-fans each user's
     * managed schedules per (member, Space) so shared-Space users are covered —
     * the migration/repair action for Space-aware background tasks. Slow (scans
     * all subscribers), so a long timeout is used.
     * @returns {Promise<object>} Sweep report
     *   { tasksProcessed, totalAdded, totalRemoved, totalUpdated, errors, timestamp }.
     */
    async resyncAllSchedules() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/analysis-tasks/sweep', {}, { timeout: 300000 });
        return response.data;
    },

    /**
     * Gets scheduled users for an analysis task with enriched data.
     * @param {string} taskId - The task ID.
     * @param {object} params - Query params (search).
     * @returns {Promise<{schedules: Array, summary: object}>}
     */
    async getTaskScheduleDetails(taskId, params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/analysis-tasks/${taskId}/schedules`, { params, timeout: 60000 });
        return response.data;
    },

    /**
     * Pauses a user's managed schedule for a task.
     * @param {string} taskId - The task ID.
     * @param {string} uid - The user UID.
     * @returns {Promise<{success: boolean, scheduleId: string}>}
     */
    async pauseTaskUserSchedule(taskId, uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/analysis-tasks/${taskId}/schedules/${uid}/pause`);
        return response.data;
    },

    /**
     * Resumes a user's managed schedule for a task.
     * @param {string} taskId - The task ID.
     * @param {string} uid - The user UID.
     * @returns {Promise<{success: boolean, scheduleId: string}>}
     */
    async resumeTaskUserSchedule(taskId, uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/analysis-tasks/${taskId}/schedules/${uid}/resume`);
        return response.data;
    },

    /**
     * Triggers immediate execution of a user's managed schedule for a task.
     * @param {string} taskId - The task ID.
     * @param {string} uid - The user UID.
     * @returns {Promise<object>}
     */
    async triggerTaskUserSchedule(taskId, uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/analysis-tasks/${taskId}/schedules/${uid}/trigger`);
        return response.data;
    },

    /**
     * Triggers a reconciliation sweep across all active tasks.
     * @returns {Promise<object>} Sweep results.
     */
    async sweepAllTasks() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/analysis-tasks/sweep', {}, { timeout: 120000 });
        return response.data;
    },

    // ==================== PER-USER AI ANALYSIS API (Issue #71) ====================

    /**
     * Fetches AI execution history for a specific user.
     * @param {string} uid - The user's UID.
     * @param {object} params - Query params (limit, offset, taskId, status, from, to).
     * @returns {Promise<{executions: Array, total: number, limit: number, offset: number}>}
     */
    async getUserAIExecutions(uid, params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}/ai-executions`, { params });
        return response.data;
    },

    /**
     * Fetches AI analysis status summary for a specific user.
     * @param {string} uid - The user's UID.
     * @returns {Promise<object>} Status with notification prefs, subscription limits, anti-spam, stats, task eligibility.
     */
    async getUserAIStatus(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}/ai-status`);
        return response.data;
    },

    /**
     * Fetches AI-generated notifications for a specific user.
     * @param {string} uid - The user's UID.
     * @param {object} params - Query params (limit, offset).
     * @returns {Promise<{notifications: Array, total: number, summary: object}>}
     */
    /**
     * Updates a user's trial period.
     * @param {string} uid - The user's UID.
     * @param {object} data - { action: 'extend'|'reduce'|'expire'|'reset', days?: number }
     * @returns {Promise<object>} Trial status after update.
     */
    async updateUserTrial(uid, data) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/users/${uid}/trial`, data);
        return response.data;
    },

    async getUserAINotifications(uid, params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/users/${uid}/ai-notifications`, { params });
        return response.data;
    },

    // ==================== AUTOPILOT ADMIN API ====================

    async getAutopilotOverview() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/autopilot/overview');
        return response.data;
    },

    async getAutopilotUsers(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/autopilot/users', { params });
        return response.data;
    },

    async getAutopilotUserDetail(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/autopilot/users/${uid}`);
        return response.data;
    },

    async getAutopilotUserActivity(uid, params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/autopilot/users/${uid}/activity`, { params });
        return response.data;
    },

    async getAutopilotUserCycles(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/autopilot/users/${uid}/cycles`);
        return response.data;
    },

    async getAutopilotUserEscalations(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/autopilot/users/${uid}/escalations`);
        return response.data;
    },

    async pauseAutopilotUser(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/autopilot/users/${uid}/pause`);
        return response.data;
    },

    async resumeAutopilotUser(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/autopilot/users/${uid}/resume`);
        return response.data;
    },

    async forceRecommendAutopilotUser(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/autopilot/users/${uid}/force-recommend`);
        return response.data;
    },

    async releaseForcedModeAutopilotUser(uid) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/autopilot/users/${uid}/release-forced-mode`);
        return response.data;
    },

    async triggerAutopilotCycle(uid, taskId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/autopilot/users/${uid}/trigger-cycle`, { taskId });
        return response.data;
    },

    async clearAutopilotEscalation(uid, entityId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/autopilot/users/${uid}/clear-escalation`, { entityId });
        return response.data;
    },

    async overrideAutopilotLearning(uid, data) {
        const client = await createAuthenticatedClient();
        const response = await client.put(`/admin/autopilot/users/${uid}/learning`, data);
        return response.data;
    },

    // ─── OTP Phone Management ──────────────────────────────────

    async getOTPPhones(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/otp-phones', { params });
        return response.data;
    },

    async getOTPPhoneDetail(phoneNumber) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/otp-phones/${encodeURIComponent(phoneNumber)}`);
        return response.data;
    },

    async setPhoneReuse(phoneNumber, { maxUses, note }) {
        const client = await createAuthenticatedClient();
        const response = await client.put(`/admin/otp-phones/${encodeURIComponent(phoneNumber)}/reuse`, { maxUses, note });
        return response.data;
    },

    async deletePhoneReuse(phoneNumber) {
        const client = await createAuthenticatedClient();
        const response = await client.delete(`/admin/otp-phones/${encodeURIComponent(phoneNumber)}/reuse`);
        return response.data;
    },

    async releasePhone(phoneNumber, uid = null) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/otp-phones/${encodeURIComponent(phoneNumber)}/release`, uid ? { uid } : {});
        return response.data;
    },

    async backfillPhoneRegistry() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/otp-phones/backfill');
        return response.data;
    },

    // --- Issue #233: Admin Sync Observability ---

    async getAdminSyncs(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/syncs', { params });
        return response.data;
    },

    async getAdminSyncDetail(uid, syncId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/syncs/${uid}/${syncId}`);
        return response.data;
    },

    async downloadAdminSyncCSV(uid, syncId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/syncs/${uid}/${syncId}/csv`, {
            responseType: 'blob',
        });
        return response.data;
    },

    async backfillSyncIndex() {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/syncs/backfill');
        return response.data;
    },

    async getAdminSyncLogs(uid, syncId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/syncs/${uid}/${syncId}/logs`);
        return response.data;
    },

    async downloadAdminSyncLogsCSV(uid, syncId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/syncs/${uid}/${syncId}/logs/csv`, {
            responseType: 'blob',
        });
        return response.data;
    },

    // ==================== GEO TARGET REGISTRY ====================

    /** Per-platform geo registry status + joint coverage breakdown. */
    async getGeoSummary() {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/geo/summary');
        return response.data;
    },

    /**
     * Start a geo registry import (or dry-run preview).
     * @param {object} opts { platforms?: ('google'|'microsoft')[], dryRun?: boolean }
     * @returns {Promise<object>} dryRun → { stats }; otherwise → { jobId, status }
     */
    async importGeo({ platforms, dryRun = false } = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.post('/admin/geo/import', { platforms, dryRun });
        return response.data;
    },

    /** Poll a background geo import job. */
    async getGeoImportJob(jobId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/geo/import/${jobId}`);
        return response.data;
    },

    /**
     * Inspect the registry. Country-scoped search, or exact lookup by criterionId/locationId.
     * @param {object} params { q, countryCode, platform, criterionId, locationId }
     */
    async searchGeo(params = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get('/admin/geo/search', { params });
        return response.data;
    },

    // ==================== ADMIN PLATFORM CONNECT (OAuth) ====================

    /** Connection status for the admin's platform OAuth. */
    async getIntegrationStatus(platformId) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/integrations/${platformId}/status`);
        return response.data;
    },

    /** Get the OAuth authorize URL to start a connect (admin redirects the browser to it). */
    async getIntegrationAuthUrl(platformId, { accountType } = {}) {
        const client = await createAuthenticatedClient();
        const response = await client.get(`/admin/integrations/${platformId}/auth-url`, {
            params: accountType ? { accountType } : {},
        });
        return response.data;
    },

    /** Complete OAuth after the provider redirects back with code+state. */
    async completeIntegrationOAuth(platformId, { code, state }) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/integrations/${platformId}/complete-oauth`, { code, state });
        return response.data;
    },

    /** Disconnect the admin's platform OAuth. */
    async disconnectIntegration(platformId) {
        const client = await createAuthenticatedClient();
        const response = await client.post(`/admin/integrations/${platformId}/disconnect`);
        return response.data;
    },

};
